import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag } from 'antd';

import { setAppTitle } from 'src/store/app';
import {
  fetchViewers,
  isLoadingViewersSelector,
  viewersListSelector,
  viewersMetaDataSelector
} from 'src/store/viewer';

import './viewers.scss';

const POLL_INTERVAL_MS = 5000;

export const Viewers = () => {
  const { t } = useTranslation('viewers');
  const dispatch = useDispatch();

  const viewers = useSelector(viewersListSelector);
  const viewersMeta = useSelector(viewersMetaDataSelector);
  const isLoading = useSelector(isLoadingViewersSelector);

  const [pagination, setPagination] = useState({ page: 1, perPage: 20 });
  const [sorting, setSorting] = useState({ sortBy: 'lastSeenAt', sortOrder: 'desc' });

  useEffect(() => {
    dispatch(setAppTitle(t('menu:viewers')));
  }, [dispatch, t]);

  useEffect(() => {
    const payload = {
      page: pagination.page,
      perPage: pagination.perPage,
      ...(sorting.sortBy ? { sortBy: sorting.sortBy } : {}),
      ...(sorting.sortOrder ? { sortOrder: sorting.sortOrder } : {})
    };

    dispatch(fetchViewers(payload));

    const intervalId = setInterval(() => {
      dispatch(fetchViewers(payload));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [dispatch, pagination, sorting]);

  const onTableChange = useCallback(
    (tablePagination, filters, sorter) => {
      const sorterValue = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextSorting =
        sorterValue?.field && sorterValue?.order
          ? {
              sortBy: sorterValue.field,
              sortOrder: sorterValue.order === 'ascend' ? 'asc' : 'desc'
            }
          : { sortBy: null, sortOrder: null };

      const sortChanged =
        sorting.sortBy !== nextSorting.sortBy || sorting.sortOrder !== nextSorting.sortOrder;

      setSorting(nextSorting);

      setPagination({
        page: sortChanged ? 1 : tablePagination?.current || 1,
        perPage: tablePagination?.pageSize || 20
      });
    },
    [sorting.sortBy, sorting.sortOrder]
  );

  const columns = useMemo(
    () => [
      {
        title: t('table.login'),
        dataIndex: 'login',
        key: 'login',
        sorter: true,
        sortOrder: sorting.sortBy === 'login' ? `${sorting.sortOrder}end` : null
      },
      {
        title: t('table.follower'),
        dataIndex: 'isFollower',
        key: 'isFollower',
        width: 130,
        sorter: true,
        sortOrder: sorting.sortBy === 'isFollower' ? `${sorting.sortOrder}end` : null,
        render: (isFollower) => {
          if (isFollower === true) return <Tag color="blue">{t('labels.follower')}</Tag>;
          if (isFollower === false) return <Tag>{t('labels.viewer')}</Tag>;
          return <Tag>{t('labels.unknown')}</Tag>;
        }
      },
      {
        title: t('table.watch_time_hours'),
        dataIndex: 'watchTimeHours',
        key: 'watchTimeHours',
        width: 160,
        sorter: true,
        sortOrder: sorting.sortBy === 'watchTimeHours' ? `${sorting.sortOrder}end` : null,
        render: (value) => Number(value || 0).toFixed(1)
      },
      {
        title: t('table.watch_streak'),
        dataIndex: 'watchStreak',
        key: 'watchStreak',
        width: 130,
        sorter: true,
        sortOrder: sorting.sortBy === 'watchStreak' ? `${sorting.sortOrder}end` : null,
        render: (value) => Number(value || 0)
      },
      {
        title: t('table.giveaway_watch_hours'),
        dataIndex: 'watchTimeGiveawayHours',
        key: 'watchTimeGiveawayHours',
        width: 170,
        sorter: true,
        sortOrder: sorting.sortBy === 'watchTimeGiveawayHours' ? `${sorting.sortOrder}end` : null,
        render: (value) => Number(value || 0).toFixed(1)
      },
      {
        title: t('table.giveaway_watch_streak'),
        dataIndex: 'watchStreakGiveaway',
        key: 'watchStreakGiveaway',
        width: 170,
        sorter: true,
        sortOrder: sorting.sortBy === 'watchStreakGiveaway' ? `${sorting.sortOrder}end` : null,
        render: (value) => Number(value || 0)
      },
      {
        title: t('table.last_seen'),
        dataIndex: 'lastSeenAt',
        key: 'lastSeenAt',
        width: 220,
        sorter: true,
        sortOrder: sorting.sortBy === 'lastSeenAt' ? `${sorting.sortOrder}end` : null,
        render: (value) => {
          if (!value) return t('labels.never');
          return new Date(value).toLocaleString();
        }
      }
    ],
    [sorting.sortBy, sorting.sortOrder, t]
  );

  return (
    <div className="viewers">
      <div className="viewers__content">
        <Card className="viewers__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id}
            columns={columns}
            dataSource={viewers}
            loading={isLoading}
            onChange={onTableChange}
            pagination={{
              current: viewersMeta?.page || pagination.page,
              pageSize: viewersMeta?.perPage || pagination.perPage,
              total: viewersMeta?.total || 0,
              showSizeChanger: true
            }}
          />
        </Card>
      </div>
    </div>
  );
};
